import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import {
  MIN_OPTIONS,
  MAX_TOTAL_OPTIONS,
  type HostEvent,
  type ManageOptions,
} from '../../shared/protocol';
import { OptionPicker, type DraftOption } from './OptionPicker';

/**
 * Inline "edit the movies" form for the host dashboard — add, rename, swap art, or
 * remove ballot options after the event exists. Reuses OptionPicker (same TMDB/fan-art
 * lookup as the create form). Each row carries its server `id` ALONGSIDE the draft (not
 * inside it) because OptionPicker rebuilds the draft object on change and would drop an
 * embedded id; the worker reconciles by id (kept options preserve their votes).
 */
interface Row {
  id?: string; // server id of an existing option; absent = a freshly added one
  draft: DraftOption;
}

export function EditMovies({
  event,
  token,
  onSaved,
  onCancel,
}: {
  event: HostEvent;
  token: string;
  onSaved: (ev: HostEvent) => void;
  onCancel: () => void;
}) {
  const [rows, setRows] = useState<Row[]>(() =>
    event.options.map((o) => ({
      id: o.id,
      draft: {
        title: o.title,
        year: o.year,
        posterUrl: o.posterUrl,
        tmdbId: o.tmdbId,
        overview: o.overview,
        hint: o.hint,
      },
    })),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setDraft = (i: number, v: DraftOption) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, draft: v } : r)));
  const addRow = () =>
    setRows((prev) => (prev.length < MAX_TOTAL_OPTIONS ? [...prev, { draft: { title: '' } }] : prev));
  const removeRow = (i: number) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    setError(null);
    const cleaned = rows
      .map((r) => ({ ...r, draft: { ...r.draft, title: r.draft.title.trim() } }))
      .filter((r) => r.draft.title.length > 0);
    if (cleaned.length < MIN_OPTIONS) {
      setError('Keep at least one movie on the ballot.');
      return;
    }
    const body: ManageOptions = {
      v: 1,
      options: cleaned.map((r) => ({
        ...(r.id ? { id: r.id } : {}),
        title: r.draft.title,
        year: r.draft.year,
        posterUrl: r.draft.posterUrl,
        tmdbId: r.draft.tmdbId,
        overview: r.draft.overview,
        hint: r.draft.hint,
      })),
    };
    setSaving(true);
    try {
      const ev = await api.setOptions(event.id, token, body);
      onSaved(ev);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the movies.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <h2>🎬 Edit movies</h2>
      <p className="soft">Add, rename, swap the art, or remove options. Everyone's ballot updates instantly.</p>
      <div className="notice warn">
        Removing a movie that someone already voted for clears that vote — they'll just pick again.
      </div>

      {rows.map((r, i) => (
        <OptionPicker
          key={r.id ?? `new-${i}`}
          index={i}
          value={r.draft}
          onChange={(v) => setDraft(i, v)}
          onRemove={() => removeRow(i)}
          canRemove={rows.length > MIN_OPTIONS}
        />
      ))}

      {rows.length < MAX_TOTAL_OPTIONS && (
        <button className="btn ghost small" onClick={addRow}>
          + Add another movie
        </button>
      )}

      {error && <div className="notice error">{error}</div>}

      <div className="row" style={{ marginTop: 14 }}>
        <button className="btn" onClick={save} disabled={saving}>
          {saving ? <span className="spinner" /> : 'Save movies'}
        </button>
        <button className="btn ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );
}
